import styled from "styled-components";
import StyledLink from "../../atoms/StyledLink";
import properties from "../../../data/properties";
import { useParams } from "react-router-dom";

const Page = styled.main`
    width: 100%;
    background: #f7f7f7;
    min-height: 100vh;
`;

const Hero = styled.section`
    position: relative;
    width: 100%;
    height: 90vh;
    min-height: 650px;
    overflow: hidden;

    @media (max-width: 900px) {
        height: 70vh;
        min-height: 500px;
    }
`;

const HeroImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const Overlay = styled.div`
    position: absolute;
    inset: 0;
    background: linear-gradient(
        rgba(0, 0, 0, 0.15),
        rgba(0, 0, 0, 0.45)
    );
`;

const HeroContent = styled.div`
    position: absolute;
    bottom: 80px;
    left: 50%;
    transform: translateX(-50%);
    width: min(1200px, 90%);
    color: white;
`;

const Title = styled.h1`
    font-size: clamp(2.6rem, 6vw, 4.8rem);
    font-weight: 300;
    margin: 0;
    letter-spacing: 2px;
`;

const Location = styled.p`
    margin-top: 18px;
    font-size: 1.25rem;
    opacity: 0.95;
`;

const Price = styled.h2`
    margin-top: 28px;
    font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 500;
`;

const Wrapper = styled.div`
    width: min(1200px, 90%);
    margin: 0 auto;
`;

const FactsSection = styled.section`
    padding: 90px 0;
`;

const FactsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const FactCard = styled.div`
    background: white;
    border-radius: 18px;
    padding: 32px;
    box-shadow: 0 12px 35px rgba(0, 0, 0, 0.08);
    text-align: center;
`;

const FactLabel = styled.div`
    color: #888;
    font-size: 0.95rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 16px;
`;

const FactValue = styled.div`
    font-size: 2rem;
    font-weight: 600;
    color: #222;
`;

const Section = styled.section`
    padding-bottom: 90px;
`;

const SectionTitle = styled.h2`
    font-size: 2.2rem;
    font-weight: 300;
    margin-bottom: 40px;
    letter-spacing: 1px;
`;

const Description = styled.div`
    max-width: 900px;

    p {
        color: #555;
        font-size: 1.1rem;
        line-height: 2;
        margin-bottom: 24px;
    }

    ul {
        margin: 0;
        padding-left: 20px;
        color: #555;
        line-height: 2;
    }
`;

const DetailsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 18px 50px;

    @media (max-width: 700px) {
        grid-template-columns: 1fr;
    }
`;

const DetailRow = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 18px 0;
    border-bottom: 1px solid #e7e7e7;
`;

const DetailLabel = styled.span`
    color: #777;
`;

const DetailValue = styled.span`
    font-weight: 500;
    color: #222;
`;

const Gallery = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 650px) {
        grid-template-columns: 1fr;
    }
`;

const GalleryImage = styled.img`
    width: 100%;
    height: 320px;
    object-fit: cover;
    border-radius: 14px;
    transition: 0.35s;

    &:hover {
        transform: scale(1.02);
    }
`;

const CTA = styled.div`
    padding: 80px 0 120px;
    display: flex;
    justify-content: center;
`;

function PropertyPage() {
    const { id } = useParams();

    const property = properties.find(
        (property) => property.id === id
    );

    if (!property) {
        return <Page>Immobilie nicht gefunden.</Page>;
    }

    return (
        <Page>
            <Hero>
                <HeroImage
                    src={property.heroImage}
                    alt={property.title}
                />

                <Overlay />

                <HeroContent>
                    <Title>{property.title}</Title>

                    <Location>{property.location}</Location>

                    {property.price && (
                        <Price>{property.price}</Price>
                    )}
                </HeroContent>
            </Hero>

            <Wrapper>
                <FactsSection>
                    <FactsGrid>
                        {(property.facts ?? []).map((fact) => (
                            <FactCard key={fact.label}>
                                <FactLabel>{fact.label}</FactLabel>
                                <FactValue>{fact.value}</FactValue>
                            </FactCard>
                        ))}
                    </FactsGrid>
                </FactsSection>

                {property.details && (
                    <Section>
                        <SectionTitle>Eckdaten</SectionTitle>

                        <DetailsGrid>
                            {property.details.map((item) => (
                                <DetailRow key={item.label}>
                                    <DetailLabel>{item.label}</DetailLabel>
                                    <DetailValue>{item.value}</DetailValue>
                                </DetailRow>
                            ))}
                        </DetailsGrid>
                    </Section>
                )}

                {property.gallery && (
                    <Section>
                        <SectionTitle>Galerie</SectionTitle>

                        <Gallery>
                            {property.gallery.map((image, index) => (
                                <GalleryImage
                                    key={index}
                                    src={image}
                                    alt={`${property.title} ${index + 1}`}
                                />
                            ))}
                        </Gallery>
                    </Section>
                )}

                {property.sections?.map((section, index) => (
                    <Section key={index}>
                        <SectionTitle>{section.title}</SectionTitle>

                        <Description>
                            {section.paragraphs &&
                                section.paragraphs.map((p, i) => (
                                    <p key={i}>{p}</p>
                                ))}

                            {section.list && (
                                <ul>
                                    {section.list.map((item, i) => (
                                        <li key={i}>{item}</li>
                                    ))}
                                </ul>
                            )}
                        </Description>
                    </Section>
                ))}

                <CTA>
                    <StyledLink
                        destination="/immobilien"
                        content="Zurück zur Übersicht"
                    />
                </CTA>
            </Wrapper>
        </Page>
    );
}

export default PropertyPage;